import styled, { keyframes } from 'styled-components';

const sheen = keyframes`
  0%, 60% { left: -80%; }
  100% { left: 130%; }
`;

const rot = keyframes`
  to { transform: rotate(360deg); }
`;

/* The signup flow's dark page with one white card, as designed in ZOOMAA-AI/tigaly-signup-flow (flow.css). */
export const StyledSignupFlow = styled.div`
  & {
    --bg: #121212;
    --t1: #f5f5f7;
    --t2: #b3b3b8;
    --t3: #8e8e93;
    --brand: #ff6b1a;
    --brand-hi: #ff8640;
    --brand-ink: #1a0a00;
    --ink: #1d1d1f;
    --ink2: #3a3a3c;
    --ink3: #6e6e73;
    --field: #d2d2d7;
    --bad: #df1b41;
    --ease: cubic-bezier(0.2, 0.8, 0.2, 1);
    --sans:
      -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    --gutter: 16px;
  }
  @media (min-width: 720px) {
    & {
      --gutter: 40px;
    }
  }
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  [hidden] {
    display: none !important;
  }
  & {
    min-height: 100vh;
    background: var(--bg);
    color: var(--t1);
    font-family: var(--sans);
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
  a {
    color: inherit;
  }
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  }
  :focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 3px;
  }
  ::selection {
    background: rgba(255, 107, 26, 0.38);
  }

  .band {
    position: absolute;
    z-index: -1;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(
        ellipse 55% 60% at 78% 18%,
        rgba(255, 107, 26, 0.34),
        rgba(255, 107, 26, 0.1) 45%,
        transparent 75%
      ),
      radial-gradient(ellipse 45% 40% at 12% 0%, rgba(255, 134, 64, 0.12), transparent 70%);
  }
  .band::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px) 0 0 / 28px 28px;
    -webkit-mask-image: radial-gradient(ellipse 70% 60% at 60% 20%, #000, transparent 75%);
    mask-image: radial-gradient(ellipse 70% 60% at 60% 20%, #000, transparent 75%);
  }
  .shell {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .top {
    width: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 24px var(--gutter) 0;
  }
  .logo {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    text-decoration: none;
  }
  .logo svg {
    width: 26px;
    height: 26px;
  }
  .wm {
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: #fff;
  }
  .wm span {
    color: var(--brand);
  }

  .grid {
    flex: 1;
    width: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: clamp(28px, 6vh, 64px) var(--gutter) 48px;
    display: grid;
    gap: 28px;
    align-items: start;
  }
  @media (min-width: 900px) {
    .grid {
      grid-template-columns: 1fr 460px;
      gap: 72px;
      align-items: center;
    }
  }

  .left h2 {
    font-size: clamp(30px, 3.6vw, 44px);
    line-height: 1.02;
    font-weight: 800;
    letter-spacing: -0.045em;
    color: #fff;
    text-wrap: balance;
  }
  .why {
    list-style: none;
    display: grid;
    gap: 22px;
    margin-top: 30px;
  }
  .why li {
    display: grid;
    grid-template-columns: 40px 1fr;
    gap: 14px;
    align-items: start;
  }
  .ic {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.08);
    color: var(--t1);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  }
  .ic svg {
    width: 20px;
    height: 20px;
  }
  .why b {
    display: block;
    font-size: 16px;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #fff;
  }
  .why p {
    margin-top: 3px;
    font-size: 14.5px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.8);
  }

  .card {
    padding: clamp(24px, 4vw, 38px);
    border-radius: 18px;
    background: #fff;
    color: var(--ink);
    box-shadow:
      0 30px 80px -24px rgba(0, 0, 0, 0.6),
      0 0 0 1px rgba(0, 0, 0, 0.04);
  }
  .card h1 {
    font-size: 24px;
    line-height: 1.2;
    font-weight: 750;
    letter-spacing: -0.03em;
  }
  .card-h {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .sub {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--ink3);
  }
  .sub b {
    color: var(--ink);
    font-weight: 650;
  }
  .link {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink2);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  form {
    margin-top: 22px;
  }
  .f {
    display: block;
  }
  .f + .f,
  .f + .grp,
  .grp + .f {
    margin-top: 16px;
  }
  .lbl {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink2);
  }
  .in {
    display: block;
    width: 100%;
    height: 46px;
    margin-top: 7px;
    padding: 0 13px;
    border-radius: 9px;
    font: inherit;
    font-size: 16px;
    color: var(--ink);
    background: #fff;
    border: 1px solid var(--field);
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
    -webkit-appearance: none;
    appearance: none;
  }
  .in::placeholder {
    color: #a1a1a6;
  }
  .in:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 4px rgba(255, 107, 26, 0.2);
    position: relative;
    z-index: 1;
  }
  .in[aria-invalid='true'] {
    border-color: var(--bad);
  }
  .in[readonly] {
    background: #f5f5f7;
    color: var(--ink2);
  }
  .in:disabled {
    background: #fff;
    opacity: 1;
    cursor: not-allowed;
  }
  select.in {
    padding-right: 36px;
    background: #fff
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5l3 3 3-3' fill='none' stroke='%236E6E73' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")
      no-repeat right 13px center / 12px;
  }
  .hint {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: var(--bad);
  }
  .err {
    display: none;
    margin-top: 14px;
    padding: 10px 13px;
    border-radius: 9px;
    font-size: 14px;
    color: #b42318;
    background: #fef3f2;
    box-shadow: inset 0 0 0 1px #fecdca;
  }
  .err.show {
    display: block;
  }

  .grp .box {
    margin-top: 7px;
    border-radius: 9px;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  }
  .grp .in {
    margin: 0;
    box-shadow: none;
  }
  .num {
    position: relative;
  }
  .num .in {
    border-radius: 9px 9px 0 0;
    padding-right: 128px;
  }
  .brands {
    position: absolute;
    z-index: 2;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    gap: 5px;
    pointer-events: none;
  }
  .brands svg {
    width: 32px;
    height: 21px;
    border-radius: 4px;
    transition:
      opacity 0.2s,
      filter 0.2s;
  }
  .brands.picked svg {
    opacity: 0.25;
    filter: grayscale(1);
  }
  .brands.picked svg.on {
    opacity: 1;
    filter: none;
  }
  .pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .pair .in {
    margin-top: -1px;
    border-radius: 0 0 0 9px;
  }
  .pair .in + .in {
    margin-left: -1px;
    border-radius: 0 0 9px 0;
  }

  .btn {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    width: 100%;
    height: 52px;
    margin-top: 22px;
    padding: 0 24px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
    white-space: nowrap;
    text-decoration: none;
    color: var(--brand-ink);
    background: var(--brand);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.35),
      0 10px 26px -10px rgba(255, 107, 26, 0.9);
    transition:
      transform 0.2s var(--ease),
      background 0.2s,
      box-shadow 0.25s;
  }
  .btn:hover {
    background: var(--brand-hi);
    transform: translateY(-1px);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.35),
      0 14px 34px -10px rgba(255, 107, 26, 1);
  }
  .btn:active {
    transform: scale(0.98);
  }
  .btn::after {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    bottom: 0;
    left: -80%;
    width: 55%;
    pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    transform: skewX(-18deg);
    animation: ${sheen} 5s var(--ease) infinite;
  }
  .btn svg {
    width: 17px;
    height: 17px;
  }
  .btn .spin {
    display: none;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-right-color: transparent;
    animation: ${rot} 0.7s linear infinite;
  }
  .btn.busy .spin {
    display: block;
  }
  .btn.busy .go,
  .btn.ok .go {
    display: none;
  }
  .btn .yes {
    display: none;
  }
  .btn.ok {
    background: #12a150;
    color: #fff;
    box-shadow: none;
  }
  .btn.ok .yes {
    display: block;
  }
  .btn[disabled] {
    pointer-events: none;
  }
  .fine {
    margin-top: 14px;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
    color: var(--ink3);
  }
  .fine a {
    color: var(--ink2);
    text-underline-offset: 2px;
  }
  .trust {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px 16px;
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid #ececee;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink2);
  }
  .trust span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .trust svg {
    width: 14px;
    height: 14px;
    color: #12a150;
  }
  .powered {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 12px;
    font-size: 12.5px;
    color: var(--ink3);
  }
  .powered svg {
    width: 13px;
    height: 13px;
  }
  .powered b {
    font-size: 14.5px;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: var(--ink2);
  }
  .test {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ink2);
    background: #f0f0f2;
    white-space: nowrap;
  }
  .done-msg {
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 14px;
    line-height: 1.45;
    color: #05603a;
    background: #ecfdf3;
    box-shadow: inset 0 0 0 1px #abefc6;
  }

  .try {
    font-size: 15px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.82);
  }
  .big {
    margin-top: 6px;
    font-size: clamp(44px, 5.4vw, 64px);
    line-height: 0.95;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #fff;
  }
  .then {
    margin-top: 10px;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.86);
  }
  .plans {
    display: inline-grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    margin-top: 22px;
    padding: 4px;
    border-radius: 14px;
    background: rgba(18, 18, 18, 0.55);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
  }
  .plans button {
    padding: 9px 16px;
    border-radius: 11px;
    text-align: left;
    transition: background 0.2s;
  }
  .plans button:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  .plans b {
    display: block;
    font-size: 14.5px;
    font-weight: 700;
    color: #fff;
  }
  .plans small {
    display: block;
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.7);
  }
  .plans button[aria-checked='true'] {
    background: #fff;
  }
  .plans [aria-checked='true'] b {
    color: var(--ink);
  }
  .plans [aria-checked='true'] small {
    color: var(--ink3);
  }
  .lines {
    max-width: 440px;
    margin-top: 26px;
  }
  .lines div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.82);
    border-top: 1px solid rgba(255, 255, 255, 0.14);
  }
  .lines dd {
    font-weight: 650;
    color: #fff;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .lines .tot {
    font-weight: 700;
    color: #fff;
  }
  .lines .tot dd {
    font-size: 17px;
    color: #30d158;
  }
  .lines .tot dd::before {
    content: '✓ ';
  }
  .inc {
    list-style: none;
    display: grid;
    gap: 8px;
    margin-top: 22px;
  }
  .inc li {
    position: relative;
    padding-left: 24px;
    font-size: 14.5px;
    color: rgba(255, 255, 255, 0.82);
  }
  .inc li::before {
    content: '✓';
    position: absolute;
    left: 0;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.6);
  }

  .solo {
    flex: 1;
    width: 100%;
    display: grid;
    place-items: center;
    padding: clamp(28px, 7vh, 72px) var(--gutter) 48px;
  }
  .code {
    width: 100%;
    max-width: 420px;
    text-align: center;
  }
  .code h1 {
    font-size: clamp(34px, 4.6vw, 48px);
    line-height: 1;
    font-weight: 800;
    letter-spacing: -0.045em;
    color: #fff;
  }
  .code .sub {
    margin-top: 12px;
    font-size: 16px;
    color: var(--t2);
    overflow-wrap: anywhere;
  }
  .code .sub b {
    color: var(--t1);
  }
  .code form {
    margin-top: 30px;
  }
  .otp {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  .otp .in {
    height: 76px;
    margin: 0;
    padding: 0;
    border-radius: 14px;
    text-align: center;
    font-size: 32px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #fff;
    caret-color: var(--brand);
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.16);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
  .otp .in:focus {
    background: rgba(255, 255, 255, 0.09);
    border-color: var(--brand);
    box-shadow: 0 0 0 4px rgba(255, 107, 26, 0.22);
  }
  .otp .in[aria-invalid='true'] {
    border-color: #ff6961;
  }
  .code .err {
    text-align: left;
    color: #ffb4ab;
    background: rgba(255, 105, 97, 0.1);
    box-shadow: inset 0 0 0 1px rgba(255, 105, 97, 0.3);
  }
  .code .btn {
    margin-top: 18px;
  }
  .resend {
    margin-top: 16px;
    font-size: 14px;
    color: var(--t3);
  }
  .resend + .resend {
    margin-top: 6px;
  }
  .code .link {
    color: var(--t1);
    font-weight: 600;
  }
  .code .link[disabled] {
    color: var(--t3);
    text-decoration: none;
    cursor: default;
    font-weight: 500;
  }
  .test-code {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 22px;
    font-size: 13.5px;
    color: var(--t2);
  }
  .test-code b {
    font-weight: 750;
    letter-spacing: 0.08em;
    color: #fff;
  }
  .test-code .test {
    color: var(--t1);
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 899px) {
    .su .left {
      order: 2;
    }
    .su .left h2 {
      display: none;
    }
    .su .why {
      margin-top: 0;
      gap: 18px;
    }
    .su .why p {
      color: var(--t2);
    }
    .pw .inc {
      display: none;
    }
    .pw .lines {
      margin-top: 18px;
    }
  }

  .foot {
    width: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 0 var(--gutter) 28px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 10px 18px;
    font-size: 12.5px;
    color: var(--t3);
  }
  .foot nav {
    display: flex;
    gap: 18px;
  }
  .foot a {
    text-decoration: none;
  }
  .foot a:hover {
    color: var(--t1);
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation: none !important;
      transition: none !important;
    }
  }

  h1,
  h2 {
    text-transform: none;
  }
  .google svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  .logo img {
    width: 26px;
    height: 26px;
  }
  .google {
    width: 100%;
    height: 46px;
    margin-top: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-radius: 9px;
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    border: 1px solid var(--field);
    background: #fff;
  }
  .google:hover {
    background: #f5f5f7;
  }
  .or {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 18px;
    font-size: 12.5px;
    color: var(--ink3);
  }
  .or::before,
  .or::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #ececee;
  }
  .or + form {
    margin-top: 14px;
  }
  .pay-element {
    margin-top: 16px;
  }

  /* The app's code panel (VerifyCodePanel), drawn on the dark code screen. */
  .code .vc-label,
  .code .vc-resend-text {
    color: var(--t2);
  }
  .code .vc-timer {
    color: var(--t2);
    background: rgba(255, 255, 255, 0.08);
  }
  .code [role='group'] {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  .code input.code-digit {
    width: 100%;
    height: 76px;
    border-radius: 14px;
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    caret-color: var(--brand);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.16);
  }
  .code input.code-digit:focus {
    border-color: var(--brand);
    box-shadow: 0 0 0 4px rgba(255, 107, 26, 0.22);
  }
  .code .vc-submit,
  .code .vc-expired button {
    width: 100%;
    height: 52px;
    margin-top: 18px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 700;
    color: var(--brand-ink);
    background: var(--brand);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.35),
      0 10px 26px -10px rgba(255, 107, 26, 0.9);
  }
  .code .vc-submit:disabled {
    opacity: 0.55;
  }
  .code .vc-resend {
    background: transparent;
    border-color: rgba(255, 255, 255, 0.14);
  }
  .code .vc-resend-btn {
    color: var(--t1);
  }
  .code .vc-expired {
    color: var(--t2);
    background: rgba(255, 255, 255, 0.06);
  }
`;
